import { useEffect, useRef, useState, type ReactElement } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { LANGUAGES, setLang, t, useLang, type Lang } from '@/lib/i18n';
import { cn } from '@/lib/format';

/**
 * Flags are inline SVG, not emoji: Windows has no flag emoji (it shows "DE"
 * letters), so emoji flags only work on Mac. 3:2 viewBox throughout.
 */
const FLAGS: Record<Lang, ReactElement> = {
  en: (
    <svg viewBox="0 0 60 40" aria-hidden="true">
      <clipPath id="fl-gb"><path d="M0 0v40h60V0z" /></clipPath>
      <g clipPath="url(#fl-gb)">
        <path d="M0 0v40h60V0z" fill="#012169" />
        <path d="M0 0l60 40m0-40L0 40" stroke="#fff" strokeWidth="8" />
        <path d="M0 0l60 40m0-40L0 40" stroke="#C8102E" strokeWidth="3" />
        <path d="M30 0v40M0 20h60" stroke="#fff" strokeWidth="12" />
        <path d="M30 0v40M0 20h60" stroke="#C8102E" strokeWidth="7" />
      </g>
    </svg>
  ),
  de: (
    <svg viewBox="0 0 60 40" aria-hidden="true">
      <path d="M0 0h60v13.34H0z" fill="#000" />
      <path d="M0 13.33h60v13.34H0z" fill="#DD0000" />
      <path d="M0 26.66h60V40H0z" fill="#FFCE00" />
    </svg>
  ),
  es: (
    <svg viewBox="0 0 60 40" aria-hidden="true">
      <path d="M0 0h60v40H0z" fill="#AA151B" />
      <path d="M0 10h60v20H0z" fill="#F1BF00" />
    </svg>
  ),
  fr: (
    <svg viewBox="0 0 60 40" aria-hidden="true">
      <path d="M0 0h20v40H0z" fill="#002654" />
      <path d="M20 0h20v40H20z" fill="#fff" />
      <path d="M40 0h20v40H40z" fill="#CE1126" />
    </svg>
  ),
  it: (
    <svg viewBox="0 0 60 40" aria-hidden="true">
      <path d="M0 0h20v40H0z" fill="#009246" />
      <path d="M20 0h20v40H20z" fill="#fff" />
      <path d="M40 0h20v40H40z" fill="#CE2B37" />
    </svg>
  ),
  nl: (
    <svg viewBox="0 0 60 40" aria-hidden="true">
      <path d="M0 0h60v13.34H0z" fill="#AE1C28" />
      <path d="M0 13.33h60v13.34H0z" fill="#fff" />
      <path d="M0 26.66h60V40H0z" fill="#21468B" />
    </svg>
  ),
};

export function Flag({ lang, className }: { lang: Lang; className?: string }) {
  return (
    <span className={cn('inline-block h-3.5 w-[21px] shrink-0 overflow-hidden rounded-[2px] shadow-[0_0_0_1px_rgba(0,0,0,0.15)] [&>svg]:h-full [&>svg]:w-full', className)}>
      {FLAGS[lang]}
    </span>
  );
}

/**
 * Language picker with flags. The choice is remembered per browser; without
 * one the app follows the browser language (see lib/i18n).
 */
export function LanguageSwitch({ tone = 'bar', className }: { tone?: 'bar' | 'light'; className?: string }) {
  const lang = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('languageSwitch.label')}
        title={t('languageSwitch.label')}
        className={cn(
          'flex h-8 items-center gap-1.5 rounded-md px-2 text-[12px] font-semibold uppercase focus:outline-none focus-visible:ring-2',
          tone === 'bar'
            ? 'text-gray-600 hover:bg-gray-100 focus-visible:ring-brand-500 dark:text-gray-300 dark:hover:bg-white/10'
            : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 focus-visible:ring-brand-500',
        )}
      >
        <Flag lang={lang} />
        {lang}
        <ChevronDown className="size-3.5 opacity-70" />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label={t('languageSwitch.label')}
          className="absolute right-0 top-full z-50 mt-1 min-w-[150px] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 text-[13px] text-gray-800 shadow-lg dark:border-white/10 dark:bg-gray-800 dark:text-gray-100"
        >
          {(Object.keys(LANGUAGES) as Lang[]).map((l) => (
            <li key={l} role="option" aria-selected={l === lang}>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setLang(l);
                }}
                className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-white/10"
              >
                <Flag lang={l} />
                <span className="flex-1">{LANGUAGES[l]}</span>
                {l === lang && <Check className="size-3.5 text-brand-600" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
